'use client'

import { memo, useCallback, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

const FAQ_ITEMS = [
  {
    question: 'What is NINK?',
    answer:
      'NINK is a tray-first desktop app for system-wide voice writing. It ships Ink Flow (Dictate, Instruct, Select & Ask, Chain) without depending on NINX Local.',
  },
  {
    question: 'Where is my data stored?',
    answer:
      'On your machine. Whisper/Gemma weights install into Tauri app data after an explicit Download. Session history stays local. Optional Google API keys use Keychain (macOS) or Credential Manager (Windows).',
  },
  {
    question: 'Does it download models automatically?',
    answer:
      'No. Fresh installs ship with no Whisper or Gemma weights. Model transfers start only when you click Download, with pinned revisions and SHA-256 checks.',
  },
  {
    question: 'Is audio sent to the cloud?',
    answer:
      'Raw Dictate captures and transcribes on-device. Local Gemma rewrite stays local. Google AI is optional BYOK and only runs after you explicitly select it. NINK never falls back from local to cloud.',
  },
  {
    question: 'How is NINK different from NINX?',
    answer:
      'NINX is the full private desktop agent (chat, tools, automations, voice). NINK is focused: tray popover + Ink Flow writing everywhere. Choose NINK when you only need dictation.',
  },
  {
    question: 'Which apps does NINK work in?',
    answer:
      'Any app with a text cursor. Slack, WhatsApp, Gmail, Docs, Notes, your IDE, and more. NINK pastes at the frontmost cursor; it does not open its own editor.',
  },
  {
    question: 'Which platforms are supported?',
    answer:
      'macOS and Windows. Global hotkeys, paste-at-cursor, selection replace, and the floating HUD are built for both.',
  },
] as const

export const NinkFaqSection = memo(function NinkFaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0)

  const toggle = useCallback((index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index))
  }, [])

  return (
    <div className="mx-auto max-w-3xl px-6">
      <div className="mb-10 text-center">
        <div className="nink-hybrid-eyebrow mb-3 justify-center">FAQ</div>
        <h2 className="nink-hybrid-display ninxl-theme-text text-2xl md:text-4xl">
          Common questions
        </h2>
      </div>

      <div className="nink-hybrid-card overflow-hidden">
        <div className="divide-y divide-[color:var(--ninxl-theme-border-soft,var(--ninxl-8bit-dim))]">
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openIndex === index
            return (
              <div key={item.question} className="px-5 md:px-6">
                <button
                  type="button"
                  onClick={() => toggle(index)}
                  className="flex w-full items-center justify-between gap-4 border-0 bg-transparent py-5 text-left cursor-pointer"
                  aria-expanded={isOpen}
                >
                  <span className="nink-hybrid-body ninxl-theme-text text-sm font-semibold md:text-base">
                    {item.question}
                  </span>
                  <Plus
                    className={cn(
                      'h-4 w-4 shrink-0 ninxl-theme-text-muted transition-transform duration-200',
                      isOpen && 'rotate-45',
                    )}
                    aria-hidden
                  />
                </button>
                {isOpen ? (
                  <p className="nink-hybrid-body pb-5 pr-8 text-left text-sm md:text-base">
                    {item.answer}
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
})
